import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Save, Timer, PackageCheck, Hourglass, AlertTriangle, Ban, Search, MapPin } from "lucide-react";
import { useImportJob } from "@/hooks/useImportJob";
import { ImportProgress } from "@/components/ImportProgress";
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Line, Legend } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";

export default function SLA() {
  const [stats, setStats] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [meta, setMeta] = useState(24);
  const [q, setQ] = useState("");
  const [cidade, setCidade] = useState("");
  const fileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    const params = { q: q || undefined, cidade: cidade || undefined };
    const [s, p] = await Promise.all([
      api.get("/sla/stats", { params: { cidade: cidade || undefined } }),
      api.get("/sla/pedidos", { params }),
    ]);
    setStats(s.data);
    setMeta(s.data.meta_horas);
    setPedidos(p.data);
  }, [q, cidade]);

  useEffect(() => { const t = setTimeout(() => fetchAll().catch(() => toast.error("Erro ao carregar SLA")), 250); return () => clearTimeout(t); }, [fetchAll]);
  const { job, start, running: importing } = useImportJob((r) => { toast.success(`${r.imported} pedidos importados · ${r.excluidos} excluídos do cálculo (base substituída)`); fetchAll(); });

  const saveMeta = async () => {
    try {
      await api.put("/sla/meta", { meta_horas: Number(meta) });
      toast.success("Meta atualizada");
      fetchAll();
    } catch { toast.error("Erro ao salvar meta"); }
  };

  const importFile = (e) => {
    const f = e.target.files?.[0];
    if (f) start("/sla/import", f);
    e.target.value = "";
  };

  if (!stats) return <div className="py-20 text-center text-[#5A6B62]" data-testid="sla-loading">Carregando SLA...</div>;

  const kpis = [
    { id: "sla-kpi-tempo-medio", title: "Tempo Médio de Expedição", value: `${stats.tempo_medio}h`, icon: Timer, color: stats.tempo_medio <= stats.meta_horas ? "#059669" : "#DC2626", sub: `Meta: < ${stats.meta_horas}h` },
    { id: "sla-kpi-dentro-sla", title: "% Dentro do SLA", value: `${stats.pct_dentro_sla}%`, icon: PackageCheck, color: "#2563EB", sub: `${stats.total} pedidos no total` },
    { id: "sla-kpi-aguardando", title: "Aguardando Coleta", value: stats.aguardando, icon: Hourglass, color: "#B8860B", sub: "Sem data de coleta" },
    { id: "sla-kpi-fora-sla", title: "Fora do SLA", value: stats.fora_sla, icon: AlertTriangle, color: "#DC2626", sub: "Acima da meta" },
    { id: "sla-kpi-excluidos", title: "Excluídos do cálculo", value: stats.excluidos.total, icon: Ban, color: "#6B7280", sub: stats.excluidos.por_status.map((x) => `${x.status}: ${x.qtd}`).join(" · ") || "Devolvido / Insucesso / Fora da abrangência / Não liberado" },
  ];

  return (
    <div className="space-y-6" data-testid="sla-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2A1F]">SLA de Expedição</h1>
          <p className="text-sm text-[#5A6B62] mt-1">Lead time Faturamento → Coleta · base Ordem de serviço{stats.coleta_antecipada ? ` · ${stats.coleta_antecipada} coletas antes do faturamento (contadas como 0h)` : ""}</p>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" data-testid="sla-import-input" onChange={importFile} />
          <button data-testid="sla-import-btn" onClick={() => fileRef.current?.click()} disabled={importing}
            className="bg-white hover:bg-[#F1F5F2] text-[#0F2A1F] border border-[#D9E3DC] font-medium px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60">
            <Upload className="w-4 h-4" /> {importing ? "Importando..." : "Importar Ordem de serviço (.xlsx/.csv)"}
          </button>
        </div>
      </div>
      {job && <div className="bg-white border border-[#D9E3DC] rounded-lg p-3"><ImportProgress job={job} testid="sla-import-progress" /></div>}

      {/* Filtro por cidade */}
      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 text-[#0F2A1F]">
          <MapPin className="w-4 h-4 text-[#0F6B3A]" />
          <span className="text-sm font-semibold">Filtrar por cidade</span>
        </div>
        <select data-testid="sla-cidade-filter"
          value={cidade}
          onChange={(e) => setCidade(e.target.value)}
          className="flex-1 sm:flex-none sm:min-w-[280px] bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]">
          <option value="">Todas as cidades ({(stats.cidades_disponiveis || []).length})</option>
          {(stats.cidades_disponiveis || []).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {cidade && (
          <button data-testid="sla-cidade-clear" onClick={() => setCidade("")}
            className="text-xs text-[#0F6B3A] hover:text-[#0B5530] font-semibold underline underline-offset-2">
            Limpar filtro
          </button>
        )}
        {cidade && (
          <span className="text-xs text-[#5A6B62] ml-auto">
            {stats.total} pedidos · média {stats.tempo_medio}h
          </span>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {kpis.map((k) => (
          <div key={k.id} data-testid={k.id} className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-[#5A6B62] uppercase tracking-wide">{k.title}</p>
                <p className="text-3xl font-extrabold text-[#0F2A1F] mt-2">{k.value}</p>
                <p className="text-xs text-[#8A9A90] mt-1 line-clamp-2" title={k.sub}>{k.sub}</p>
              </div>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${k.color}18` }}>
                <k.icon className="w-5 h-5" style={{ color: k.color }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Meta */}
      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-5 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <h2 className="text-base font-semibold text-[#0F2A1F]">Configuração de Meta SLA</h2>
          <p className="text-xs text-[#5A6B62] mt-1">A linha de referência do gráfico é atualizada imediatamente.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs font-semibold text-[#0F2A1F]">Meta (horas)</label>
            <input data-testid="sla-target-input" type="number" min={1} step={0.5} value={meta} onChange={(e) => setMeta(e.target.value)}
              className="mt-1 w-32 bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]" />
          </div>
          <button data-testid="sla-save-meta-btn" onClick={saveMeta}
            className="bg-[#0F6B3A] hover:bg-[#0B5530] text-white font-medium px-4 py-2 rounded-lg shadow-sm flex items-center gap-2 transition-colors">
            <Save className="w-4 h-4" /> Salvar
          </button>
        </div>
      </div>

      {/* Gráfico */}
      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-5">
        <h2 className="text-base font-semibold text-[#0F2A1F] mb-4">Tempo Médio Diário vs Meta SLA</h2>
        <div className="h-72" data-testid="sla-chart-daily">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={stats.chart_daily} margin={{ top: 5, right: 10, left: -14, bottom: 0 }}>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="data" tick={{ fontSize: 10, fill: "#8A9A90" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#8A9A90" }} unit="h" />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D9E3DC", borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [`${v}h`, "Tempo médio"]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="tempo_medio" name="Tempo médio (h)" fill="#0F6B3A" radius={[4, 4, 0, 0]} maxBarSize={28} />
              <ReferenceLine y={Number(meta)} stroke="#DC2626" strokeDasharray="6 4" strokeWidth={2}
                label={{ value: `Meta ${meta}h`, position: "insideTopRight", fill: "#DC2626", fontSize: 11, fontWeight: 700 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Por loja */}
        <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F1F5F2]">
            <h2 className="text-base font-semibold text-[#0F2A1F]">Tempo Médio por Cidade</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sla-cidade-table">
              <thead>
                <tr className="bg-[#F4F8F5] border-b border-[#D9E3DC] text-xs font-bold text-[#0F2A1F] uppercase tracking-wider">
                  <th className="text-left py-3 px-4">Cidade</th>
                  <th className="text-left py-3 px-4">Pedidos</th>
                  <th className="text-left py-3 px-4">Tempo Médio</th>
                  <th className="text-left py-3 px-4">Situação</th>
                </tr>
              </thead>
              <tbody>
                {stats.por_cidade.map((l) => (
                  <tr key={l.cidade} className="border-b border-[#F1F5F2] hover:bg-[#F4F8F5]/80" data-testid={`sla-cidade-row-${l.cidade}`}>
                    <td className="py-3 px-4 font-semibold text-[#0F2A1F]">{l.cidade}</td>
                    <td className="py-3 px-4 text-[#5A6B62]">{l.pedidos}</td>
                    <td className="py-3 px-4 text-[#5A6B62]">{l.tempo_medio}h</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${l.tempo_medio <= stats.meta_horas ? "bg-[#ECFDF5] border-[#A7F3D0] text-[#047857]" : "bg-[#FEF2F2] border-[#FECACA] text-[#B91C1C]"}`}>
                        {l.tempo_medio <= stats.meta_horas ? "Dentro da meta" : "Acima da meta"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pedidos */}
        <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F1F5F2]">
            <h2 className="text-base font-semibold text-[#0F2A1F]">Pedidos — Lead Time</h2>
            <div className="relative mt-2"><Search className="w-4 h-4 text-[#8A9A90] absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="sla-busca-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pedido, NF, cidade ou status..." className="w-full bg-white border border-[#D9E3DC] rounded-md pl-9 pr-3 py-2 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]" /></div>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="sla-pedidos-table">
              <thead className="sticky top-0 bg-[#F4F8F5]">
                <tr className="border-b border-[#D9E3DC] text-xs font-bold text-[#0F2A1F] uppercase tracking-wider">
                  <th className="text-left py-3 px-4">Pedido</th>
                  <th className="text-left py-3 px-4">Cidade</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Faturado</th>
                  <th className="text-left py-3 px-4">Coleta</th>
                  <th className="text-left py-3 px-4">Lead (h)</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.slice(0, 300).map((p) => (
                  <tr key={p.id} className={`border-b border-[#F1F5F2] hover:bg-[#F4F8F5]/80 ${p.excluido ? "opacity-60" : ""}`}>
                    <td className="py-2.5 px-4 font-mono text-xs font-semibold text-[#0F2A1F]">{p.pedido}{p.nf ? <span className="block text-[10px] text-[#8A9A90] font-normal">NF {p.nf}</span> : null}</td>
                    <td className="py-2.5 px-4 text-[#5A6B62] text-xs">{p.cidade || p.loja}</td>
                    <td className="py-2.5 px-4 text-xs"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${p.excluido ? "bg-[#F3F4F6] text-[#6B7280]" : "bg-[#EAF7EF] text-[#0F6B3A]"}`}>{p.status || "—"}</span></td>
                    <td className="py-2.5 px-4 text-[#8A9A90] text-xs font-mono">{p.faturado ? p.faturado.slice(0, 16).replace("T", " ") : "—"}</td>
                    <td className="py-2.5 px-4 text-[#8A9A90] text-xs font-mono">{p.coletado ? p.coletado.slice(0, 16).replace("T", " ") : "Pendente"}</td>
                    <td className={`py-2.5 px-4 font-semibold text-xs ${p.lead_time_h == null ? "text-[#B8860B]" : p.lead_time_h <= stats.meta_horas ? "text-[#047857]" : "text-[#B91C1C]"}`}>
                      {p.lead_time_h == null ? "—" : `${p.lead_time_h}h`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
